// Achievements wall — the learner's badge collection. CLAUDE.md §25.
// Earned badges are bright; locked ones are greyed with a lock. Kid-facing:
// large touch targets, playful, i18n-driven (AR default), RTL via logical props.

import { useLocale, useT } from '@fajrarabi/i18n/react';
import { Skeleton } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useAuth } from '~/lib/auth';
import { listBadges, listCertificates, type BadgeDto, type CertificateDto } from '~/lib/learn';
import { asLocale } from '~/lib/locale';
import { shellForContext } from '~/lib/roles';

export const Route = createFileRoute('/$locale/badges')({
  component: BadgesPage,
});

// iconKey → emoji. Keys are seeded in db/seeds/badges.ts; unknown falls back.
const ICONS: Record<string, string> = {
  seedling: '🌱',
  books: '📚',
  rocket: '🚀',
  star: '⭐',
  sparkles: '✨',
  flame: '🔥',
  fire: '🔥',
  crown: '👑',
  gem: '💎',
  trophy: '🏆',
  medal: '🏅',
};

function BadgesPage() {
  const t = useT();
  const navigate = useNavigate();
  const { locale: rawLocale } = useParams({ from: '/$locale/badges' });
  const locale = asLocale(rawLocale);
  const { currentRoleContext } = useAuth();

  // Learner-only surface (the API 403s anyone else anyway).
  const isKid = shellForContext(currentRoleContext) === 'kid';
  useEffect(() => {
    if (!isKid) {
      void navigate({ to: '/$locale', params: { locale }, replace: true });
    }
  }, [isKid, navigate, locale]);

  const query = useQuery({
    queryKey: ['kids', 'badges'],
    queryFn: ({ signal }) => listBadges(signal),
    enabled: isKid,
  });

  if (!isKid) return null;

  const badges = query.data ?? [];
  const earned = badges.filter((b) => b.earnedAt !== null).length;

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-2xl font-bold text-burgundy-700">{t('app.badges.title')}</h1>
      {badges.length > 0 && (
        <p className="mt-1 text-sm font-semibold text-ink-500">
          {t('app.badges.subtitle', { earned, total: badges.length })}
        </p>
      )}

      {query.isLoading ? (
        <div className="mt-6 grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="aspect-square w-full rounded-3xl" />
          ))}
        </div>
      ) : badges.length === 0 ? (
        <p className="mt-8 rounded-3xl bg-cream-50 p-8 text-center text-ink-500">
          {t('app.badges.empty')}
        </p>
      ) : (
        <ul className="mt-6 grid grid-cols-3 gap-4 sm:grid-cols-4">
          {badges.map((b) => (
            <BadgeTile key={b.id} badge={b} locale={locale} />
          ))}
        </ul>
      )}

      <CertificatesSection locale={locale} enabled={isKid} />
    </div>
  );
}

// Units the learner finished → a printable certificate each (CLAUDE.md §25).
function CertificatesSection({ locale, enabled }: { locale: 'ar' | 'en'; enabled: boolean }) {
  const t = useT();
  const query = useQuery({
    queryKey: ['kids', 'certificates'],
    queryFn: ({ signal }) => listCertificates(signal),
    enabled,
  });
  const certs = query.data ?? [];
  if (certs.length === 0) return null;

  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold text-burgundy-700">{t('app.certificate.section_title')}</h2>
      <ul className="mt-4 space-y-3">
        {certs.map((c: CertificateDto) => (
          <li key={c.unitId}>
            <Link
              to="/$locale/certificate/$unitId"
              params={{ locale, unitId: c.unitId }}
              className="flex min-h-[64px] items-center gap-3 rounded-3xl bg-cream-50 p-4 ring-1 ring-cream-200 transition-colors hover:ring-sun-300"
            >
              <span className="text-3xl" aria-hidden="true">
                📜
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-bold text-ink-900">
                  <bdi>{t(c.unitTitleKey)}</bdi>
                </span>
                <span className="block text-xs text-ink-500">
                  {t('app.certificate.lessons_done', { n: c.lessonCount })}
                </span>
              </span>
              <span className="text-sm font-bold text-burgundy-600">
                {t('app.certificate.view')}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function BadgeTile({ badge, locale }: { badge: BadgeDto; locale: 'ar' | 'en' }) {
  const t = useT();
  const { isRtl } = useLocale();
  const isEarned = badge.earnedAt !== null;
  const icon = ICONS[badge.iconKey ?? ''] ?? '🏅';

  const earnedLabel = isEarned
    ? t('app.badges.earned_on', {
        date: new Date(badge.earnedAt!).toLocaleDateString(locale === 'ar' ? 'ar' : 'en-GB'),
      })
    : t('app.badges.locked');

  return (
    <li>
      <div
        className={`flex aspect-square flex-col items-center justify-center rounded-3xl p-3 text-center transition-transform ${
          isEarned
            ? 'bg-gradient-to-b from-sun-50 to-cream-50 shadow-[0_16px_36px_-24px_rgba(31,26,23,0.4)] ring-2 ring-sun-300/60 hover:scale-105'
            : 'bg-cream-50 ring-1 ring-cream-200'
        }`}
        title={earnedLabel}
      >
        <span
          className={`text-4xl ${isEarned ? '' : 'opacity-25 grayscale'}`}
          aria-hidden="true"
          dir={isRtl ? 'rtl' : 'ltr'}
        >
          {isEarned ? icon : '🔒'}
        </span>
        <span
          className={`mt-2 text-xs font-bold leading-tight ${
            isEarned ? 'text-burgundy-700' : 'text-ink-500'
          }`}
        >
          {t(badge.titleKey)}
        </span>
      </div>
      <p className="sr-only">
        {t(badge.titleKey)} — {earnedLabel}
      </p>
    </li>
  );
}
